<template>
  <b-modal
    v-model="editorShown"
    centered
    ref="form-modal"
    :title="formId ? 'Edit Diagnosis Code' : 'Add Diagnosis Code'"
    header-bg-variant="primary"
    header-text-variant="white"
    @ok.prevent="submitDiagnosisCode"
    no-close-on-backdrop
    header-class="bg-purple"
    size="md-lg"
  >
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel()">
        Cancel
      </b-button>
      <b-button variant="primary" @click="ok()">
        Save
      </b-button>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="ICD-CODE*"
          :state="formErrors.icdCodeId.length == 0 ? null : false"
          :invalid-feedback="formErrors.icdCodeId.join(', ')"
        >
          <v-select
            class=""
            :class="icdCodes.length == 0 ? 'icd-code-picker-hide-options' : ''"
            placeholder="Search for ICD Codes"
            v-model="form.icdCodeId"
            :options="icdCodes"
            @search="fetchIcdCodeOptions"
            :filterable="false"
            ><template #no-options="{ search }">
              {{
                search && search.length > 3 ? "Sorry, no matching options" : ""
              }}
            </template>
            <template #open-indicator>
              <span></span>
            </template>
          </v-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group label="Status">
          <b-form-select
            placeholder=""
            v-model="form.status"
            :options="statuses"
          />
        </b-form-group>
        <b-form-group
          label="Resolved by"
          v-if="showResolvedByInput"
          :state="formErrors.resolvedBy.length == 0 ? null : false"
          :invalid-feedback="formErrors.resolvedBy.join(', ')"
        >
          <date-picker
            v-model="form.resolvedBy"
            :errors="formErrors.resolvedBy"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Category"
          :state="formErrors.category.length == 0 ? null : false"
          :invalid-feedback="formErrors.category.join(', ')"
        >
          <b-form-select
            placeholder=""
            v-model="form.category"
            :options="categories"
            :errors="formErrors.category"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Active From"
          :state="formErrors.activeFrom.length == 0 ? null : false"
          :invalid-feedback="formErrors.activeFrom.join(', ')"
        >
          <date-picker
            v-model="form.activeFrom"
            :errors="formErrors.activeFrom"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group label="Comments">
          <b-form-textarea
            placeholder=""
            v-model="form.comments"
            rows="6"
            max-rows="10"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import DatePicker from "../controls/DatePicker.vue";
import { mapState, mapActions, mapGetters } from "vuex";
import icdCodeServer from "../../server/icdCode";
import diagnosisCodesServer from "../../server/diagnosisCode";
import { validateRequired, validateDate } from "../../helpers/validation";

export default {
  name: "EocViewAddEditDiagnosisCodesModal",
  components: {
    DatePicker
  },
  props: {
    diagnosisCodeId: {
      type: String
    },
    eocId: {
      type: String
    },
    patientId: {
      type: String
    }
  },
  data() {
    return {
      formId: null,
      form: {
        icdCodeId: null,
        status: "",
        category: "",
        activeFrom: "",
        resolvedBy: "",
        comments: ""
      },
      formErrors: {
        icdCodeId: [],
        activeFrom: [],
        resolvedBy: [],
        category: []
      },
      lastTimeout: null,
      icdCodes: [],
      categories: [
        {
          value: 0,
          text: "Primary"
        },
        {
          value: 1,
          text: "Secondary"
        },
        {
          value: 2,
          text: "Tertiary"
        }
      ]
    };
  },
  async mounted() {
    await this.fetchDiagnosisCodeStatuses();
    if (this.diagnosisCodeId) {
      const form = await diagnosisCodesServer.get(this.diagnosisCodeId);
      this.formId = form.id;
      this.form = {
        icdCodeId: {
          value: form.icdCode.id,
          label: `${form.icdCode.code} - ${form.icdCode.description}`
        },
        status: form.status.id,
        activeFrom: form.activeFrom,
        resolvedBy: form.resolvedBy,
        comments: form.description,
        category: this.categories.find(x => x.text == form.category).value
      };
    } else {
      this.form = {
        icdCodeId: null,
        status: this.statuses.find(status => status.data.default).value,
        activeFrom: this.formatDateOnlyMysql(this.getCurrentAdjustedDateTime()),
        resolvedBy: "",
        comments: "",
        category: ""
      };
    }
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", ["fetchDiagnosisCodeStatuses"]),
    async fetchIcdCodeOptions(search, loading) {
      if (this.lastTimeout) {
        clearTimeout(this.lastTimeout);
        this.lastTimeout = null;
      }
      if (search && search.trim().length >= 3) {
        this.icdCodes = [];
      }

      this.lastTimeout = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const icdCodes = await icdCodeServer.query(
            { searchPhrase: search, pageSize: 100 },
            {
              "Hide-Loading": true
            }
          );
          this.icdCodes = icdCodes.map(icdCode => ({
            value: icdCode.id,
            label: `${icdCode.code} - ${icdCode.description}`
          }));
        } else this.icdCodes = [];
        loading(false);
      }, 1000);
    },
    validateSubmit() {
      let withoutErrors = true;

      this.formErrors.icdCodeId = [];
      if (!validateRequired(this.form.icdCodeId)) {
        this.formErrors.icdCodeId.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.category = [];
      if (!validateRequired(this.form.category)) {
        this.formErrors.category.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.activeFrom = [];
      if (this.form.activeFrom && !validateDate(this.form.activeFrom)) {
        this.formErrors.activeFrom.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      this.formErrors.resolvedBy = [];
      if (
        this.showResolvedByInput &&
        this.form.resolvedBy &&
        !validateDate(this.form.resolvedBy)
      ) {
        this.formErrors.resolvedBy.push(
          "This field must have a valid date format"
        );
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitDiagnosisCode() {
      const result = this.validateSubmit();
      let isCreate = false;
      if (result) {
        const formData = {
          icdCodeId: this.form.icdCodeId ? this.form.icdCodeId.value : null,
          statusId: this.form.status,
          patientId: this.patientId,
          episodeOfCareId: this.eocId,
          activeFrom: this.form.activeFrom ? this.form.activeFrom : null,
          resolvedBy:
            this.form.resolvedBy &&
            this.form.status ==
              this.statuses.find(u => u.text === "Resolved").value
              ? this.form.resolvedBy
              : null,
          description: this.form.comments,
          category: this.form.category
        };
        let formId = this.formId;
        if (this.formId) {
          await diagnosisCodesServer.update(this.formId, formData);
        } else {
          let createResult = await diagnosisCodesServer.create(formData);
          formId = createResult.id;
          isCreate = true;
        }
        this.showAlertSuccess();
        this.$emit("onModify", {
          isCreate,
          id: formId,
          icdCodeDescription: this.form.icdCodeId?.label,
          status: this.statuses.find(s => s.value == this.form.status).data,
          activeFrom: formData.activeFrom,
          resolvedBy: formData.resolvedBy,
          description: formData.description,
          category: this.categories.find(x => x.value == formData.category).text
        });
      }
    }
  },
  computed: {
    ...mapGetters("layout", ["getCurrentAdjustedDateTime"]),
    ...mapState("client", {
      statuses: state =>
        state.diagnosisCodeStatuses.map(diagnosisCodeStatus => ({
          value: diagnosisCodeStatus.id,
          text: diagnosisCodeStatus.name,
          data: diagnosisCodeStatus
        }))
    }),
    showResolvedByInput() {
      return (
        this.form.status &&
        this.form.status == this.statuses.find(u => u.text === "Resolved").value
      );
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  }
};
</script>

<style></style>
